import React from 'react';
import {
  Box, Button, Divider, Drawer, IconButton, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { currencyFormatter } from '../../Helpers/CurrencyFormat';
import { tokens } from '../../../theme';

function typeLabel(type) {
  if (type === 'bulkMenuItem') return 'Bulk menu item';
  if (type === 'customIngredient') return 'Custom ingredient';
  return type || 'Recipe item';
}

function Meta({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: tokens.inkMuted,
          fontWeight: 600,
          mb: 0.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 650,
          color: tokens.ink,
          fontFamily: "'Figtree', sans-serif",
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Overlay makeup inspector — does not compete with the recipe grid for space.
 */
export default function ItemMakeupPanel({
  item,
  open,
  onClose,
  onEdit,
}) {
  const ingredients = Array.isArray(item?.ingredients) ? item.ingredients : [];
  const linked = Array.isArray(item?.menuItems) ? item.menuItems : [];
  const batchSize = item?.size != null
    ? `${item.size} ${item.itemUoM || item.sizeUoM || ''}`.trim()
    : '—';

  return (
    <Drawer
      anchor="right"
      open={open && Boolean(item)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100%',
          backgroundColor: tokens.surface,
          borderLeft: `1px solid ${tokens.border}`,
          boxShadow: '-12px 0 40px rgba(44, 36, 27, 0.08)',
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(44, 36, 27, 0.18)' },
      }}
    >
      {item && (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2.5, pt: 2.25, pb: 2 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0, pr: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: tokens.accent,
                    fontWeight: 700,
                    mb: 0.75,
                  }}
                >
                  Makeup
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Fraunces', 'Figtree', serif",
                    fontWeight: 650,
                    fontSize: 26,
                    lineHeight: 1.15,
                    color: tokens.ink,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {item.itemName}
                </Typography>
                <Typography sx={{ mt: 0.75, color: tokens.inkMuted, fontSize: 13 }}>
                  {typeLabel(item.type)}
                  {item.category ? ` · ${item.category}` : ''}
                </Typography>
              </Box>
              <IconButton
                aria-label="Close makeup"
                onClick={onClose}
                size="small"
                sx={{ color: tokens.inkMuted, mt: 0.25 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Box
              sx={{
                mt: 2.25,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                background:
                  `linear-gradient(160deg, ${tokens.bg} 0%, ${tokens.surface} 55%, ${tokens.okBg} 140%)`,
                border: `1px solid ${tokens.border}`,
              }}
            >
              <Meta
                label="Cost"
                value={item.productionCost != null ? currencyFormatter(item.productionCost, 2) : '—'}
              />
              <Meta
                label="On hand"
                value={item.sellableInventory != null ? String(item.sellableInventory) : '—'}
              />
              <Meta label="Batch" value={batchSize} />
            </Box>

            {onEdit && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={onEdit}
                sx={{ mt: 1.75 }}
              >
                Edit recipe item
              </Button>
            )}
          </Box>

          <Divider sx={{ borderColor: tokens.border }} />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.inkMuted }}>
                Ingredients
              </Typography>
              <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>{ingredients.length}</Typography>
            </Stack>

            {ingredients.length === 0 ? (
              <Typography sx={{ color: tokens.inkMuted, fontSize: 14, py: 1 }}>
                No ingredients yet.
              </Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {ingredients.map((ing, idx) => {
                  const amount = ing.units?.[0]?.amount;
                  const measurement = ing.units?.[0]?.measurement;
                  return (
                    <Box
                      component="li"
                      key={`${ing.id ?? ing.name}-${idx}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 1.5,
                        alignItems: 'center',
                        py: 1.15,
                        borderBottom: idx === ingredients.length - 1 ? 'none' : `1px solid ${tokens.border}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 14.5, fontWeight: 550, color: tokens.ink }}>
                        {ing.name || 'Unnamed'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 650,
                          color: tokens.brown,
                          fontVariantNumeric: 'tabular-nums',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {amount != null && amount !== ''
                          ? `${amount}${measurement ? ` ${measurement}` : ''}`
                          : '—'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase', color: tokens.inkMuted }}>
                  Used in
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>{linked.length}</Typography>
              </Stack>

              {linked.length === 0 ? (
                <Typography sx={{ color: tokens.inkMuted, fontSize: 14 }}>
                  Not linked to products.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {linked.map((m, idx) => (
                    <Typography
                      key={`${m.menuId}-${idx}`}
                      sx={{
                        fontSize: 14,
                        color: tokens.ink,
                        py: 0.65,
                        borderBottom: idx === linked.length - 1 ? 'none' : `1px solid ${tokens.border}`,
                      }}
                    >
                      {m.variant && m.variant !== 'Modifier'
                        ? `${m.name} · ${m.variant}`
                        : (m.name || `Item ${m.menuId}`)}
                      {(m.menuAmount != null || m.units?.[0]?.amount != null) && (
                        <Typography component="span" sx={{ color: tokens.inkMuted, ml: 1, fontSize: 13 }}>
                          {m.menuAmount ?? m.units?.[0]?.amount}
                          {' '}
                          {m.menuUoM || m.units?.[0]?.measurement || ''}
                        </Typography>
                      )}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
